import {AxiosError} from "axios";
import {PrivacyPolicy as PdfIcon} from "iconoir-react";
import {useState} from "react";
import Button from "react-bootstrap/Button";
import Col from "react-bootstrap/Col";
import FloatingLabel from "react-bootstrap/FloatingLabel";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import Spinner from "react-bootstrap/Spinner";
import {Trans, useTranslation} from "react-i18next";
import {useNavigate, useSearchParams} from "react-router-dom";
import {ApiConfig} from "../api/portal/ApiConfig";
import {PDFApi} from "../api/portal/generated";
import {CopyToClipboard} from "../common/CopyToClipboardButton";
import {PageTitle} from "../common/PageTitle";
import {RenderAlert} from "../common/RenderAlert";
import {logger} from "../utils/Logger";
import {StringUtils} from "../utils/StringUtils";
import {Utils} from "../utils/Utils";
import {PDFAuthenticatorSetupWizard} from "./PDFAuthenticatorSetupWizard";

/*
 * REST api for accessing the back-end
 */
const pdfApi = new PDFApi(ApiConfig.config());

export const PDFGeneratePassword = () =>
{
    /**
     * Navigate is used to navigate to the login page if the user is no longer authenticated
     */
    const navigate = useNavigate();
    /**
     * searchParams is used to get the code from the URL
     */
    const [searchParams] = useSearchParams();
    /**
     * The base64 encoded JSON containing the OTP parameters
     */
    const [otpCode, setOtpCode] = useState<string>(searchParams.get("d")??"");
    /**
     * The generated password
     */
    const [password, setPassword] = useState<string>();
    /**
     * The error message if some error occurred which is not a validation error
     */
    const [errorMessage, setErrorMessage] = useState<string>();
    /**
     * Set to true while generating the password
     */
    const [generating, setGenerating] = useState<boolean>(false);
    /**
     * If true, the "CipherMail PDF Authenticator" wizard will be shown
     */
    const [showSetupWizard, setShowSetupWizard] = useState<boolean>(false);
    /**
     * i18next translation hook
     */
    const {t} = useTranslation();

    const generatePassword = () =>
    {
        logger.debug("Getting password")

        if (otpCode === undefined) {
            throw Error("otpCode is not set")
        }

        setErrorMessage(undefined);
        setGenerating(true);

        pdfApi.pDFControllerGetPdfPassword(otpCode)
            .then((response) => {
                setPassword(response.data);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error generating password", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                setGenerating(false);
            });
    }

    /**
     * Is called when the Setup Wizard is closed
     */
    const onPDFAuthenticatorSetupWizardClose = () => {
        setShowSetupWizard(false);
    }

    return (
        <>
            <Row>
                <Col>
                    <div className="d-flex flex-row align-items-center border-bottom pb-2 mb-4">
                        <div className="d-flex justify-content-start">
                            <PageTitle title={t("portal.pdf-generate-password.title")} icon={<PdfIcon />}/>
                        </div>
                    </div>
                </Col>
            </Row>

            <Row>
                <Col lg={8} md={12}>
                    <h2 className="h4 text-secondary">{t('portal.pdf-generate-password.subtitle')}</h2>

                    <h3 className="h5 text-secondary mt-4">{t('portal.pdf-generate-password.step-by-step-heading')}</h3>

                    <ol className="list-group mb-3">
                        <li className="list-group-item border-0">
                            <span className="badge bg-primary rounded-pill me-2">1</span>{t('portal.pdf-generate-password.step-by-step-1')}
                        </li>
                        <li className="list-group-item border-0">
                            <span className="badge bg-primary rounded-pill me-2">2</span>
                            <Trans
                                i18nKey="portal.pdf-generate-password.step-by-step-2"
                                components={{ badge: <span className="badge bg-success"/> }}
                            />
                        </li>
                        <li className="list-group-item border-0">
                            <span className="badge bg-primary rounded-pill me-2">3</span>{t('portal.pdf-generate-password.step-by-step-3')}
                        </li>
                        <li className="list-group-item border-0">
                            <span className="badge bg-primary rounded-pill me-2">4</span>{t('portal.pdf-generate-password.step-by-step-4')}
                        </li>
                        <li className="list-group-item border-0">
                            <span className="badge bg-primary rounded-pill me-2">5</span>{t('portal.pdf-generate-password.step-by-step-5')}
                        </li>
                    </ol>
                </Col>
            </Row>
            <Row>
                <Col lg={8} md={12}>
                    <RenderAlert message={errorMessage}/>

                    {showSetupWizard && <PDFAuthenticatorSetupWizard onClose={onPDFAuthenticatorSetupWizardClose}/>}

                    <FloatingLabel label={t('portal.pdf-generate-password.otp-label')}
                                   className="text-secondary">
                        <Form.Control type="text" value={otpCode} placeholder="OTP code"
                                      onChange={e => setOtpCode(e.target.value)}/>
                    </FloatingLabel>

                    <div className="mt-4 d-flex flex-row">
                        <FloatingLabel label={t('portal.pdf-generate-password.password-label')}
                                       className="text-secondary w-100">
                            <Form.Control className="" type="text" value={password} placeholder="Password" disabled={true}
                                          readOnly={true}/>

                        </FloatingLabel>
                        <CopyToClipboard value={password}/>
                    </div>
                </Col>
            </Row>
            <Row className="mt-4">
                <Col lg={8} md={12}>
                    <Button
                        variant="success"
                        type="submit"
                        onClick={() => generatePassword()}
                        disabled={StringUtils.isBlank(otpCode)}
                    >
                        {generating && <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            aria-hidden="true"
                            className="me-1"
                        />}
                        {generating ? t('portal.pdf-generate-password.generating') : t('portal.pdf-generate-password.generate')}
                    </Button>
                </Col>
            </Row>

            <Row className="mt-4">
                <Col lg={8} md={12}>
                    <p className="fs-4 text-secondary">{t('portal.pdf-generate-password.app-title')}</p>
                    <p>
                        {t('portal.pdf-generate-password.pdf-authenticator-app-intro')}
                    </p>
                    <Button
                        className="mt-3"
                        variant="primary"
                        onClick={() => setShowSetupWizard(true)}
                    >
                        {t('portal.pdf-generate-password.setup-pdf-authenticator-button')}
                    </Button>
                </Col>
            </Row>
        </>
    )
}
